import { LogoAdelante } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ErrorAuth, iniciarSesion, modoDev, tieneSesionActiva } from '@/lib/auth';
import { Backspace, CircleNotch } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LoginSms } from './LoginSms';

const PIN_LARGO = 4;

interface FromState {
  from?: { pathname: string };
}

export function LoginPantalla() {
  const navigate = useNavigate();
  const location = useLocation();
  // Default '/' → RedirigirPorRol manda a /admin/obras (gestión) o /campo/avance
  // (campo). NO usar '/obras' (no existe esa ruta → 404 para rol campo).
  const destino = (location.state as FromState | null)?.from?.pathname ?? '/';

  const [modo, setModo] = useState<'sms' | 'pin'>('sms');
  const [usuario, setUsuario] = useState('');
  const [pin, setPin] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-submit cuando se completa el PIN.
  // Solo queremos disparar al cambiar `pin` — agregar las demás deps haría
  // que re-dispare en cada keystroke del usuario.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intencional, solo en cambio de pin
  useEffect(() => {
    if (pin.length === PIN_LARGO && usuario.trim().length >= 2 && !cargando) {
      handleSubmit();
    }
  }, [pin]);

  // Si ya hay sesión, redirigir. ⚠️ Este early-return va DESPUÉS de TODOS los hooks
  // (Rules of Hooks). Si va ANTES del useEffect, al loguear la sesión pasa a activa
  // y se renderizan menos hooks → React error #300 (el "me saca" tras el login).
  if (tieneSesionActiva()) {
    return <Navigate to={destino} replace />;
  }

  async function handleSubmit() {
    if (cargando) return;
    setError(null);
    if (usuario.trim().length < 2) {
      setError('Ingresá un nombre de usuario');
      return;
    }
    if (pin.length !== PIN_LARGO) {
      setError(`El PIN debe tener ${PIN_LARGO} dígitos`);
      return;
    }

    setCargando(true);
    try {
      const u = await iniciarSesion(usuario.trim(), pin);
      toast.success(`Bienvenido, ${u.nombre}`);
      navigate(destino, { replace: true });
    } catch (e) {
      const msg = e instanceof ErrorAuth ? e.message : 'Error al iniciar sesión';
      setError(msg);
      setPin('');
    } finally {
      setCargando(false);
    }
  }

  function presionarDigito(d: string) {
    if (pin.length >= PIN_LARGO) return;
    setPin((p) => p + d);
    setError(null);
  }

  function borrar() {
    setPin((p) => p.slice(0, -1));
    setError(null);
  }

  function limpiar() {
    setPin('');
    setError(null);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <LogoAdelante altura={36} />
          <p className="text-xs uppercase tracking-wider text-muted-foreground">ObrasControl</p>
        </div>

        <Card>
          <CardContent className="space-y-5 pt-6">
            {modoDev && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Modo desarrollo activo — el login se omite automáticamente.
              </div>
            )}

            {modo === 'sms' ? (
              <LoginSms
                onExito={() => navigate(destino, { replace: true })}
                onUsarPin={() => setModo('pin')}
              />
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="usuario" className="text-xs font-medium">
                    Usuario
                  </Label>
                  <Input
                    id="usuario"
                    type="text"
                    inputMode="text"
                    autoComplete="username"
                    placeholder="ej: admin"
                    value={usuario}
                    onChange={(e) => setUsuario(e.target.value)}
                    disabled={cargando}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium">PIN</Label>
                  <div className="flex justify-center gap-3">
                    {Array.from({ length: PIN_LARGO }, (_, i) => `pin-slot-${i}`).map((slot, i) => (
                      <div
                        key={slot}
                        className={`h-12 w-12 rounded-md border-2 transition-all ${
                          i < pin.length
                            ? 'border-adelante-primary bg-adelante-primary/10'
                            : 'border-border bg-white'
                        } flex items-center justify-center font-mono text-xl font-bold`}
                      >
                        {i < pin.length ? '•' : ''}
                      </div>
                    ))}
                  </div>
                </div>

                {/* PIN pad numérico */}
                <div className="grid grid-cols-3 gap-2">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                    <Button
                      key={d}
                      variant="outline"
                      size="lg"
                      className="h-14 text-xl font-semibold tabular-nums"
                      onClick={() => presionarDigito(d)}
                      disabled={cargando}
                      type="button"
                    >
                      {d}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-14 text-xs"
                    onClick={limpiar}
                    disabled={cargando}
                    type="button"
                  >
                    Limpiar
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-14 text-xl font-semibold tabular-nums"
                    onClick={() => presionarDigito('0')}
                    disabled={cargando}
                    type="button"
                  >
                    0
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-14"
                    onClick={borrar}
                    disabled={cargando}
                    type="button"
                    aria-label="Borrar"
                  >
                    <Backspace className="h-5 w-5" weight="bold" aria-hidden="true" />
                  </Button>
                </div>

                {error && (
                  <p className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-center text-xs text-destructive">
                    {error}
                  </p>
                )}

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={cargando || pin.length !== PIN_LARGO}
                  type="button"
                >
                  {cargando ? (
                    <>
                      <CircleNotch
                        className="mr-2 h-4 w-4 animate-spin"
                        weight="bold"
                        aria-hidden="true"
                      />
                      Iniciando sesión…
                    </>
                  ) : (
                    'Iniciar sesión'
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          {modo === 'sms' ? (
            <>
              ¿Preferís usar usuario y PIN?{' '}
              <button type="button" className="underline" onClick={() => setModo('pin')}>
                Ingresar con PIN
              </button>
            </>
          ) : (
            <>
              ¿Olvidaste tu PIN?{' '}
              <button type="button" className="underline" onClick={() => setModo('sms')}>
                Ingresar con código por SMS
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
